import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Divider, Layout, ListItem, Select, TextArea} from '@phonectas/unifon-ui-kit';
import {StyledLeftColumn, StyledRightColumn, StyledTextArea} from '../../styled';
import useUserStore from 'common/stores/user';
import useBmoStore from 'common/stores/bmoStore';
import {isStringNotEmpty, isValidPhone} from 'utils/validators';
import {
	StyledTextFieldMd,
	StyledTextFieldSm,
	StyledTextFieldXs,
} from './styled';
import EmailNotice from './EmailNotice';
import UploadFiles from 'common/components/UploadFiles';
import {createFormData} from 'utils/file';
import {TicketFormState} from './types';


const TicketForm: React.FC = () => {
	const [t] = useTranslation();
	const fetchAllCases = useBmoStore((state) => state.fetchAllCases);
	const postTicketWithAttach = useBmoStore((state) => state.postTicketWithAttach);
	const clearError = useBmoStore((state) => state.clearError);
	const user = useUserStore((state) => state.user);
	const [clearFiles, setClearFiles] = useState(false);
	const DEFAULT_FORM = useMemo<TicketFormState>(() => ({
		description: '',
		severity: '',
		ticketType: 'Incident',
		name: '',
		customer: {
			reportedByName: user?.name || '',
			reportedByPhoneNumber: user?.mobile.replace('+47', '') || '',
		},
		faultyService: {
			serviceType: 'Mobil',
			identifier: '',
		},
		identifierType: {
			id: 'MD',
		},
		zendeskRef: '',
		unifonSubject: '',
		notificationEmails: user?.email ? [user.email] : [],
		files: [],
	}), [user]);
	const [form, setForm] = useState<TicketFormState>(DEFAULT_FORM);

	function normalizeSelectValue(
		value: string | number | string[] | number[] | undefined | null
	): string {
		if (typeof value === 'string' || typeof value === 'number') return String(value);
		if (Array.isArray(value) && value.length > 0) return String(value[0]);
		return '';
	}
	const handleChange = useCallback(<K extends keyof TicketFormState>(name: K, value: TicketFormState[K]) => {
		setForm((prevForm) => ({
			...prevForm,
			[name]: value,
		}));
	}, []);

	const handleFilesChange = useCallback((field: string, files: File[]) => {
		setForm((prevForm) => ({
			...prevForm,
			[field]: files,
		}));
	}, []);

	const handleClear = useCallback(() => {
		setForm(DEFAULT_FORM);
		setClearFiles(true);
	}, [DEFAULT_FORM]);

	const submit = useCallback(async () => {
		try {
			const formData = createFormData(form);
			await postTicketWithAttach(formData, () => {
				setForm(DEFAULT_FORM);
				setClearFiles(true);
				fetchAllCases();
			});
		} catch (err) {
			console.error(err);
		}
	}, [form, postTicketWithAttach, DEFAULT_FORM, fetchAllCases]);

	useEffect(() => {
		if (clearFiles) {
			setClearFiles(false);
			clearError();
		}
	}, [clearFiles, clearError]);

	const handleNoticeChange = useCallback((name: string, value: string | string[]) => {
		handleChange(name as keyof TicketFormState, value as TicketFormState[keyof TicketFormState]);
	}, [handleChange]);

	const mobileValidator = useCallback((val: string | number) => {
		if (typeof val === 'number') val = val.toString();
		return !val || isValidPhone(val);
	}, []);
	const emptyStrValidator = useCallback((val: string | number) => {
		if (typeof val === 'number') val = val.toString();
		return !val || isStringNotEmpty(val);
	}, []);

	const isDisabled = !(
		// mobileValidator(form.customer.reportedByPhoneNumber) &&
		mobileValidator(form.faultyService.identifier) &&
		isStringNotEmpty(form.description) &&
		isStringNotEmpty(form.severity) &&
		isStringNotEmpty(form.name) &&
		isStringNotEmpty(form.ticketType) &&
		isStringNotEmpty(form.customer.reportedByName) &&
		mobileValidator(form.customer.reportedByPhoneNumber) &&
		isStringNotEmpty(form.customer.reportedByPhoneNumber)
	);

	return (
		<>
			
			<Layout
				fullHeight
				fullWidth
				nowrap
				justifyContent="start"
				gap="lg"
			>
				<StyledLeftColumn
					direction="column"
					nowrap
					fullHeight
					gap="sm"
				>
					<Layout direction="row" fullWidth nowrap>
						<StyledTextFieldMd
							name="reportedByName"
							label={t('Reporter name')}
							fullWidth
							value={form.customer.reportedByName}
							onChange={(e) => handleChange('customer', {...form.customer, reportedByName: e.target.value})}
							validator={emptyStrValidator}
							inputProps={{maxLength: 150}}
						/>
						<StyledTextFieldSm
							name="reportedByPhoneNumber"
							label={t('Reporter number')}
							fullWidth
							inputProps={{maxLength: 8}}
							value={form.customer.reportedByPhoneNumber}
							onChange={(e) => handleChange('customer', {...form.customer, reportedByPhoneNumber: e.target.value})}
						/>
						<StyledTextFieldSm
							name="zendeskRef"
							label={t('Zendesk ref')}
							placeholder={t('Optional')}
							fullWidth
							inputProps={{maxLength: 6}}
							value={form.zendeskRef}
							onChange={(e) => handleChange('zendeskRef', e.target.value)}
						/>
					</Layout>
					<Layout direction="row" fullWidth nowrap>
						<Layout direction="column" gap="none">
							<StyledTextArea
								name="description"
								value={form.description}
								onChange={(e) => handleChange('description', e.target.value)}
								label={t('Description')}
								fullWidth
								maxLength={1000}
							/>
							<Layout
								direction="row"
								gap="none"
								justifyContent="end"
								style={{width: '450px'}}
							>
								{form.description.length}/1000
							</Layout>
						</Layout>
					</Layout>
					<Layout direction="column" fullWidth gap="none" nowrap>
						<TextArea
							name="description"
							value={form.unifonSubject}
							onChange={(e) => handleChange('unifonSubject', e.target.value)}
							label={t('Unifon subject')}
							fullWidth
							inputProps={{maxLength: 200}}
							style={{height: '100px'}}
						/>
						<Layout
							direction="row"
							gap="none"
							justifyContent="end"
							fullWidth
						>
							{form.unifonSubject.length}/200
						</Layout>

					</Layout>
					<Layout direction="row" fullWidth nowrap>
						<Select
							name="severity"
							value={form.severity}
							onChange={value => handleChange('severity', normalizeSelectValue(value))}
							label={t('Severity')}
							placeholder={t('Select')}
						>
							<ListItem
								value={'A-feil'}
								primaryText={t('A-Error')}
								selected={form.severity === 'A-feil'}
								onClick={(value) => handleChange('severity', normalizeSelectValue(value))}
							/>
							<ListItem
								value={'B-feil'}
								primaryText={t('B-Error')}
								selected={form.severity === 'B-feil'}
								onClick={(value) => handleChange('severity', normalizeSelectValue(value))}
							/>
						</Select>
						<Select
							name="name"
							value={form.name}
							onChange={(value) => handleChange('name', normalizeSelectValue(value))}
							label={t('Problem Type')}
							placeholder={t('Select')}
						>
							<ListItem
								value={'Ingen tjeneste'}
								primaryText={'Ingen tjeneste'}
								selected={form.name === 'Ingen tjeneste'}
								onClick={(value) => handleChange('name', normalizeSelectValue(value))}
							/>
							<ListItem
								value={'Redusert kvalitet'}
								primaryText={'Redusert kvalitet'}
								selected={form.name === 'Redusert kvalitet'}
								onClick={(value) => handleChange('name', normalizeSelectValue(value))}
							/>
							<ListItem
								value={'Tilleggstjenester'}
								primaryText={'Tilleggstjenester'}
								selected={form.name === 'Tilleggstjenester'}
								onClick={(value) => handleChange('name', normalizeSelectValue(value))}
							/>
							<ListItem
								value={'Leveransesvikt'}
								primaryText={'Leveransesvikt'}
								selected={form.name === 'Leveransesvikt'}
								onClick={(value) => handleChange('name', normalizeSelectValue(value))}
							/>
						</Select>
					</Layout>
					<Layout direction="row" fullWidth nowrap>
						<StyledTextFieldMd
							name="identifier"
							label={t('Mobile used')}
							fullWidth
							inputProps={{maxLength: 8}}
							value={form.faultyService.identifier}
							onChange={(e) => handleChange('faultyService', {...form.faultyService, identifier: e.target.value})}
						/>
						<StyledTextFieldXs
							name="serviceType"
							label={t('Service-Type')}
							fullWidth
							disabled
							value={form.faultyService.serviceType}
						/>
						<StyledTextFieldXs
							name="ticketType"
							label={t('Ticket-Type')}
							fullWidth
							disabled
							value={form.ticketType}
						/>
					</Layout>
					<UploadFiles
						onClear={clearFiles}
						onChange={handleFilesChange}
					/>
					<Layout
						fullWidth
						nowrap
						style={{justifyContent: 'start', marginTop: '10px'}}
					>
						<Button
							variant="primary"
							disabled={isDisabled}
							onClick={submit}
						>
							{t('Submit')}
						</Button>
						<Button variant="secondary" onClick={handleClear}>{t('Clear form')}</Button>
					</Layout>
				</StyledLeftColumn>
				{/* If more options comes from Telenor */}
				<StyledRightColumn
					direction="column"
					fullHeight
					nowrap
					gap="sm"
				>
					<Layout direction="row" fullWidth nowrap>
						<EmailNotice
							form={form}
							onChange={handleNoticeChange}
							user={user}
						/>
					</Layout>
					<Divider />
					<Layout direction="row" fullWidth nowrap></Layout>
				</StyledRightColumn>
			</Layout>
		</>
	);
}

export default TicketForm;
